import { useCallback, useEffect, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";

import { useStable } from "@app/hooks/useStable";
import { useZoltarMutation } from "@app/hooks/zoltar";
import { parseZoltarGenerateAssistantJson } from "@app/services/zoltar";
import { getPrimaryReferenceKey } from "@app/views/projects/wizardSteps";

import {
  getVariationPromptTemplate,
  isStaticPrompt,
} from "./variationRequest";
import { loadingImagePrompts, promptId } from "./shared";
import { usePersistImageVariations } from "./usePersistImageVariations";

/**
 * Generates the `visualPrompt` of every variation that still lacks one.
 *
 * A variation's prompt is resolved from its type, the campaign and the
 * description of its reference images (see `getVariationPromptTemplate`):
 * templates that resolve to a plain string are already the prompt and are only
 * persisted, the rest are generated through Zoltar. Requests in flight are
 * tracked project-wide (see `loadingImagePrompts`) and released only once the
 * resulting prompt has been persisted, so the same prompt is never asked for
 * twice.
 *
 * Usable on its own: pass the template variations whose prompts are needed
 * plus the campaign/customer they are written for.
 *
 * @param {object} options
 * @param {string} options.projectId - The video/project the prompts belong to.
 * @param {Array<object>} options.variations - Template variations, memoized.
 * @param {Array<object>} [options.imageVariations] - Already persisted
 *   variations, used to tell which prompts are still missing.
 * @param {string} [options.templateId] - Template the campaign overrides read.
 * @param {object} [options.campaign] - The selected campaign, memoized.
 * @param {object} [options.customer] - The video's customer.
 * @param {(variation: object) => Array<object>} options.getReferenceAssets -
 *   A variation's reference images, in order, primary first.
 * @param {(asset: object) => boolean} [options.isLabeled] - Whether an asset
 *   has everything the prompt needs from it (from `useImageLabeling`).
 * @param {(variations: Array<object>) => Promise<void>} [options.persist] -
 *   Shared `imageVariations` writer; one is created when omitted.
 * @param {boolean} [options.enabled] - Whether prompts may be generated.
 */
export const useImagePrompts = ({
  projectId,
  variations,
  imageVariations,
  templateId,
  campaign,
  customer,
  getReferenceAssets,
  isLabeled,
  persist,
  enabled = true,
}) => {
  const ownWriter = usePersistImageVariations(projectId);
  const persistImageVariations = persist ?? ownWriter.persist;

  const handleUpdateImageVariations = useCallback(
    (data, variables) => {
      const { key, assetName, type } = variables;

      try {
        const zoltarData = parseZoltarGenerateAssistantJson(
          data,
          "Failed to parse image variation response",
        );

        const newVariations = [];

        newVariations.push({
          key,
          referenceKey: assetName,
          type,
          ...(zoltarData ?? {}),
        });

        return persistImageVariations(newVariations);
      } catch (error) {
        console.warn(
          "Error parsing audiences variations response:",
          error,
          data,
          variables,
        );
      }
    },
    [persistImageVariations],
  );

  const imagePromptBase = useZoltarMutation();

  const imagePrompt = useMutation({
    mutationFn: (variables) => imagePromptBase.mutateAsync(variables),
    onSuccess: handleUpdateImageVariations,
    // The key stays reserved until the generated prompt has been persisted,
    // which is exactly when `onSettled` runs — `onSuccess` is awaited first.
    onSettled: (data, error, variables) => {
      loadingImagePrompts.delete(promptId(projectId, variables.key));
    },
  });

  const { mutate: mutateImagePrompt } = imagePrompt;

  // The prompt requests still owed, fully resolved. Deriving them in a memo —
  // and stabilising the result by value — is what keeps the effect below from
  // re-running: every write to the video replaces the whole cache entry, so
  // `video.data` and everything derived from it changes identity on each
  // update, while the work that is actually left does not.
  const pendingPrompts = useMemo(
    () =>
      (variations ?? [])
        .filter(
          (variation) =>
            !!variation.type &&
            !imageVariations?.some(
              (v) => v.key === variation.key && !!v.visualPrompt,
            ),
        )
        // Every reference image has to be described first for LLM-generated
        // prompts — static prompts do not read `visual.labeling` and can
        // proceed immediately without waiting for Zoltar image labeling.
        .filter((variation) => {
          if (!isLabeled) {
            return true;
          }
          const visuals = getReferenceAssets(variation);
          if (
            isStaticPrompt(variation, {
              templateId,
              campaign,
              customer,
              visual: visuals[0],
              visuals,
            })
          ) {
            return true;
          }
          return visuals.every(isLabeled);
        })
        .map((variation) => {
          const visuals = getReferenceAssets(variation);
          const prompt = getVariationPromptTemplate(variation, {
            templateId,
            campaign,
            customer,
            visual: visuals[0],
            visuals,
          });

          return {
            ...(typeof prompt === "string" || prompt == null
              ? { promptTemplate: prompt }
              : prompt),
            assetName: getPrimaryReferenceKey(variation),
            key: variation.key,
            type: variation.type,
          };
        })
        // e.g. a `labeling-prompt` whose reference has not been analysed yet.
        .filter((request) => request.promptTemplate != null),
    [
      variations,
      imageVariations,
      getReferenceAssets,
      isLabeled,
      templateId,
      campaign,
      customer,
    ],
  );

  const stablePendingPrompts = useStable(pendingPrompts);

  // Generate image variation prompts
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Templates that resolve to a plain string are already the prompt: they
    // need no generation, only persisting — batched into a single write, so a
    // group of them cannot race each other.
    const staticVariations = [];

    stablePendingPrompts.forEach((request) => {
      const id = promptId(projectId, request.key);

      if (loadingImagePrompts.has(id)) {
        return;
      }

      loadingImagePrompts.add(id);

      if (typeof request.promptTemplate === "string") {
        staticVariations.push({
          key: request.key,
          referenceKey: request.assetName,
          type: request.type,
          visualPrompt: request.promptTemplate,
        });

        return;
      }

      mutateImagePrompt(request);
    });

    if (staticVariations.length > 0) {
      const release = () => {
        staticVariations.forEach((variation) => {
          loadingImagePrompts.delete(promptId(projectId, variation.key));
        });
      };

      persistImageVariations(staticVariations).then(release, release);
    }
  }, [
    enabled,
    stablePendingPrompts,
    projectId,
    persistImageVariations,
    mutateImagePrompt,
  ]);

  const { isPending, isError, error } = imagePrompt;

  return useMemo(
    () => ({
      pendingPrompts: stablePendingPrompts,
      isPending,
      isError,
      error,
    }),
    [stablePendingPrompts, isPending, isError, error],
  );
};
